import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { logException } from '@babylon/sentry'
import { UserExistsResponse } from '@babylon/consumer-api/types'
import { useFormatMessage } from '@babylon/intl'
import { ButtonSimple } from '@babylon/medkit'
import { Button, Box, Stack } from '@babylon/web-platform-ui'
import { RedirectLoginOptions } from '@auth0/auth0-react'
import { checkExistingUser } from '@/utils'
import {
  useCurrentPageContent,
  useActiveStepIndex,
  useNextAction,
  useAbTestTracked,
  useSignupRoutes,
} from '@/redux/signup/selectors'
import { useConfig, useContent } from '@/redux/partnerConfig/selectors'
import { FormRenderer, LanguageSwitcher, ProductFeatures } from '@/components'
import { Welcome } from '@/types/partnerConfig'
import { setUserProfile, setUserExists } from '@/redux/userProfile/actions'
import { useUserEmail } from '@/redux/userProfile/selectors'
import { trackTealiumEvent, trackTealiumView } from '@/tracking'
import {
  useTrackingScreenTitle,
  useAuth0Wrapper,
  useHandleExistingUser,
} from '@/hooks'
import {
  useAbTestName,
  useAbTestVariant,
} from '@/redux/productConfig/selectors'
import messages from './WelcomePage.messages'
import { setAbTestTracked } from '@/redux/signup/actions'
import welcomeImage from './assets/welcome_image.png'
import { useAuth0Config } from '@/redux/auth0Config/selectors'
import { useUserLocale } from '@/redux/userConfig/selectors'
import { setSignupProgress } from '@/redux/userConfig/actions'

const WelcomePageOIDC = () => {
  const translate = useFormatMessage()
  const dispatch = useDispatch()
  const nextAction = useNextAction()
  const handleExistingUser = useHandleExistingUser()
  const content = useCurrentPageContent()
  const partnerContent = useContent()
  const userEmail = useUserEmail()
  const abTestName = useAbTestName()
  const abTestVariant = useAbTestVariant()
  const { fields, useDnaV2Design, isAccountCreationV2 } = useConfig()
  const activeStepIndex = useActiveStepIndex()
  const { isAuthenticated, loginWithRedirect } = useAuth0Wrapper()
  const stepTrackingName = useTrackingScreenTitle()
  const [loading, setLoading] = useState<boolean>(false)
  const abTestTracked = useAbTestTracked()
  const { productFeatures } = content as Welcome
  const submitButtonText = translate(messages.next)
  const createAccountButtonText = translate(messages.createAccount)
  const loginButtonText = translate(messages.login)
  const initialFormValues = {
    email: userEmail,
  }
  const availableLanguages = Object.keys(partnerContent)
  const routes = useSignupRoutes()
  const { branding } = useAuth0Config()
  const userLocale = useUserLocale()

  useEffect(() => {
    if (isAuthenticated && userEmail) {
      dispatch(setUserExists(true))
    }
  }, [dispatch, isAuthenticated, userEmail])

  useEffect(() => {
    if (abTestName && !abTestTracked) {
      trackTealiumEvent({
        screen_title: stepTrackingName,
        eventCategory: 'ab test',
        eventAction: 'enter test',
        tealium_event: 'enter test',
        ab_test_name: abTestName,
        ab_test_variant: String(abTestVariant),
      })
      dispatch(setAbTestTracked(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abTestName])

  const onNextAction = (trackSignup: boolean = true) => {
    if (trackSignup) {
      trackTealiumEvent({
        screen_title: stepTrackingName,
        eventCategory: 'signup',
        eventAction: 'started signup',
        eventLabel: 'email',
        tealium_event: 'started signup',
      })
    }

    nextAction()
  }

  const oidcSignUp = (email?: string) => {
    trackTealiumEvent({
      screen_title: stepTrackingName,
      eventCategory: 'signup',
      eventAction: 'started signup',
      eventLabel: 'email',
      tealium_event: 'started signup',
    })

    // UK users should skip Registration page on successful registration
    // UK users should see Registration page on successful login
    // US users should see the About Me page in either case
    // v2 account creation only switched on for US partners
    const stepIncrement = isAccountCreationV2 ? 1 : 2
    const signupRedirectUrl = `${window.location.origin}${routes[stepIncrement].path}`

    const signUpProps: RedirectLoginOptions = {
      scope: 'openid clinical', // scopes granted for this app in auth0
      mode: 'sign-up', // used by identity repo to route to correct page
      prompt: 'login' as RedirectLoginOptions['prompt'], // prompts user for reauthentication
      appState: {
        redirectUrl: signupRedirectUrl, // where to return after registration
      },
      max_age: 0, // forces user to reauthenticate, otherwise auth0 will use a cookie to keep user logged in
      login_hint: email, // passes along the email entered on the welcome screen
      screen_hint: 'sign-up', // may not be needed but used to help auth0 route
      ...(!!branding && { branding }), // used to add logos to header
      ...(!!userLocale && { ui_locales: userLocale }),
      ...(!!isAccountCreationV2 && { fastTrackSignupEnabled: true }),
    }
    dispatch(setSignupProgress(activeStepIndex + stepIncrement))
    loginWithRedirect(signUpProps)
  }

  const oidcLogin = () => {
    const loginRedirectUrl = `${window.location.origin}${routes[1].path}`
    const loginProps: RedirectLoginOptions = {
      scope: 'openid',
      mode: 'sign-in',
      prompt: 'login' as RedirectLoginOptions['login'], // prompts user for reauthentication
      max_age: 0, // forces user to reauthenticate, otherwise auth0 will use a cookie to keep user logged in
      appState: {
        redirectUrl: loginRedirectUrl,
      },
      ...(!!branding && { branding }),
      ...(!!userLocale && { ui_locales: userLocale }),
    }

    trackTealiumView({
      screen_title: 'login/',
      tealium_event: 'login/',
    })

    trackTealiumEvent({
      screen_title: 'login/',
      eventCategory: 'login',
      eventAction: 'started login',
      tealium_event: 'started login',
    })
    dispatch(setSignupProgress(activeStepIndex + 1))
    loginWithRedirect(loginProps)
  }

  const onCheckExistingUserResponse = (
    data: UserExistsResponse,
    email: string
  ) => {
    const userExists = data?.exists
    dispatch(setUserExists(userExists))

    if (userExists && !isAuthenticated) {
      handleExistingUser(email)
      setLoading(false)
    } else {
      // onNextAction()
      oidcSignUp(email)
      setLoading(false)
    }
  }

  const onSubmit = async (values: typeof initialFormValues) => {
    const { email } = values

    if (email) {
      /**
       * Check if User already exists
       */
      try {
        setLoading(true)
        const response = await checkExistingUser(email)
        onCheckExistingUserResponse(response.data, email)
      } catch (error) {
        logException(error)
        onNextAction()
      }
    }

    dispatch(setUserProfile({ ...values }))
  }

  const onCreateAccount = () => {
    oidcSignUp()
  }

  if (useDnaV2Design && isAccountCreationV2) {
    return (
      <Box marginBottom={9}>
        <img src={welcomeImage} alt="Welcome" />
        <Stack spacing={4} marginTop={25}>
          <Button
            type="button"
            isFullWidth
            isLoading={false}
            data-testid="form-submit-button-register"
            variant="primary"
            onClick={onCreateAccount}
          >
            {createAccountButtonText}
          </Button>
          <Button
            type="button"
            isFullWidth
            isLoading={false}
            data-testid="form-submit-button-login"
            variant="secondary"
            onClick={oidcLogin}
          >
            {loginButtonText}
          </Button>
        </Stack>
      </Box>
    )
  }

  return (
    <>
      <ProductFeatures features={productFeatures} />
      {isAuthenticated && userEmail ? (
        <>
          {useDnaV2Design && (
            <Button
              onClick={() => onNextAction(false)}
              variant="primary"
              isFullWidth
            >
              {submitButtonText}
            </Button>
          )}
          {!useDnaV2Design && (
            <ButtonSimple onClick={() => onNextAction(false)} fullWidth>
              {submitButtonText}
            </ButtonSimple>
          )}
        </>
      ) : (
        <FormRenderer
          testId="welcome-page-form"
          fields={fields[activeStepIndex]}
          onSubmit={onSubmit}
          initialValues={initialFormValues}
          submitButtonText={submitButtonText}
          loading={loading}
          isInterimToken={false}
        />
      )}
      <LanguageSwitcher
        useDnaV2Design={Boolean(useDnaV2Design)}
        languages={availableLanguages}
      />
    </>
  )
}

export default WelcomePageOIDC
